import { useEffect, useMemo, useState } from "react"
import fp from "lodash/fp"
import { useSelector } from "react-redux"

import type { Tower } from "types/towers.types"

import { selectCurrentOrgId } from "features/store/orgSlice"
import { useLazyTowersQuery } from "features/api"

export const useAllTowers = () => {
  const orgId = useSelector(selectCurrentOrgId)
  const [allTowers, setAllTowers] = useState<Tower[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<any>(null)
  const [getTowers] = useLazyTowersQuery()

  useEffect(() => {
    if (!orgId) return
    let isMounted = true
    const fetchAll = async () => {
      setLoading(true)
      setError(null)
      try {
        let cursor: string | undefined = undefined
        let results: Tower[] = []
        do {
          const { data, error } = await getTowers(
            {
              params: { org_id: Number(orgId), limit: 100, cursor },
            },
            true,
          )
          if (error) throw error
          if (data?.results) {
            results = results.concat(data.results)
          }
          cursor = data?.next
        } while (cursor)
        if (isMounted) {
          setAllTowers(results)
        }
      } catch (err) {
        if (isMounted) setError(err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    fetchAll()
    return () => {
      isMounted = false
    }
  }, [orgId, getTowers])

  const towersById = useMemo(() => {
    return fp.keyBy((t: Tower) => t.id, allTowers)
  }, [allTowers])

  return {
    towers: allTowers,
    towersById,
    isLoading: loading,
    isError: error,
  }
}
